import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocationSelection } from "../context/LocationSelectionContext";
import { parseLocationQuery, stringifyLocationQuery } from "../common/url/locationQuery";
import { soleLocationToPreselect } from "../common/soleLocation";

/**
 * Custom hook to hydrate location selection from URL and sync selection changes to URL.
 *
 * @returns true when hydration is complete
 * 
 * @remarks
 * Responsibilities:
 * - Hydrates location selection from URL on mount
 * - Syncs location selection changes to URL
 * - Ensures URL is single source of truth for location selection
 * - Only runs hydration once per mount
 * 
 * Must be called within LocationSelectionProvider context.
 *
 * @example
 * ```tsx
 * function Page() {
 *   const isHydrated = useLocationHydration();
 *   if (!isHydrated) return <Spinner />;
 *   return <Content />;
 * }
 * ```
 * 
 * @see {@link LocationSelectionProvider} for context
 * @see {@link useLocationSelection} for accessing location data
 */
export function useLocationHydration(): boolean {
  const navigate = useNavigate();
  const location = useLocation();
  const { locations, selectedLocation, selectLocation, isLoadingLocations } = useLocationSelection();
  const hydratedRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const initialLocationIdRef = useRef<string | null>(null);
  const lastWrittenLocationIdRef = useRef<string | null>(null);

  // Capture initial URL params once on mount
  useEffect(() => {
    if (initialLocationIdRef.current !== null) return;
    const query = parseLocationQuery(location.search);
    initialLocationIdRef.current = query.locationId ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only capture URL on mount

  // Hydrate location selection from URL
  useEffect(() => {
    if (hydratedRef.current) return;

    const locationId = initialLocationIdRef.current;
    
    // If no locationId in URL, mark as hydrated immediately (nothing to hydrate)
    if (!locationId) {
      hydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // Wait for locations to load - distinguish between loading and loaded
    if (isLoadingLocations) return; // Still loading
    if (!locations || locations.length === 0) {
      // Loaded but empty - mark as hydrated (nothing to select)
      hydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // If already selected and matches URL, we're done
    if (selectedLocation && selectedLocation.id === locationId) {
      hydratedRef.current = true;
      setIsHydrated(true);
      return;
    }

    // Try to find and select the location
    const found = locations.find((loc) => loc.id === locationId);
    if (found) {
      selectLocation(found);
      // Don't set hydrated yet - wait for selectedLocation to update
      return;
    }

    // Location not found in list - mark as hydrated anyway
    hydratedRef.current = true;
    setIsHydrated(true);
  }, [locations, selectedLocation, selectLocation, isLoadingLocations]);

  // Mark as hydrated once selectedLocation matches the URL
  useEffect(() => {
    if (hydratedRef.current) return;
    
    const locationId = initialLocationIdRef.current;
    if (!locationId) return;
    
    // If selectedLocation now matches the URL, hydration is complete
    if (selectedLocation && selectedLocation.id === locationId) {
      hydratedRef.current = true;
      setIsHydrated(true);
    }
  }, [selectedLocation]);

  // The only location is preselected (PO, 2026-10-16): a tenant with one site
  // otherwise opens the Standorte tab on an empty picker. Once per mount and
  // only after the URL had its say, so a deep link still wins and a customer
  // who clears the choice is not overruled.
  const soleSelectedRef = useRef(false);
  useEffect(() => {
    if (!isHydrated || soleSelectedRef.current) return;
    const sole = soleLocationToPreselect(locations, selectedLocation);
    if (sole) selectLocation(sole);
    if (locations) soleSelectedRef.current = true;
  }, [isHydrated, locations, selectedLocation, selectLocation]);

  // Sync selected location to URL (after hydration)
  useEffect(() => {
    if (!hydratedRef.current) return; // Don't sync until after hydration
    
    const query = parseLocationQuery(location.search);
    const urlLocationId = query.locationId;
    const contextLocationId = selectedLocation?.id;
    
    // If we just wrote this value to the URL, skip updating again
    if (contextLocationId && lastWrittenLocationIdRef.current === contextLocationId) {
      return;
    }
    
    // If URL and context are in sync, do nothing
    if (urlLocationId === contextLocationId) return;
    
    // Update URL to match context
    const newQuery = {
      ...query,
      locationId: contextLocationId,
    };
    const newSearch = stringifyLocationQuery(newQuery);
    
    // Track the value we're writing to prevent infinite loops
    lastWrittenLocationIdRef.current = contextLocationId ?? null;
    
    navigate(
      { pathname: location.pathname, search: newSearch ? `?${newSearch}` : "" },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, location.pathname, navigate]);
  // Note: location.search intentionally removed from dependencies to prevent infinite loop
  // The effect should only run when selectedLocation changes, not when URL changes

  return isHydrated;
}
